import React from "react";
import InputKMQ from "../../UI/Input/InputKMQ";
import SelectKMQ from "../../UI/Select/SelectKMQ";
import TextArea from "../../UI/TextArea/TextArea";
import Checkbox from "../../UI/Checkbox/Checkbox";
import CheckIcon from "../../../assets/icons/UI/icons/checkmark.svg";
import ResumeDates from "../CplApplicantProfile/components/ResumeDates/ResumeDates";
import { WarningCircle } from "@phosphor-icons/react";
import NormalizationOption from "../CplApplicantProfile/components/NormalizationOption/NormalizationOption";
import FileUpload, { IAttachment } from "../CplApplicantProfile/components/FileUpload";

interface SubmittedWorkExperienceSectionProps {
  jobs: any[];
  editMode: boolean;
  locationOptions: { label: string; value: string }[];
  currOptions: any;
  isRecommendationError: boolean;
  updateJob: (index: number, field: string, value: any) => void;
  handleChooseNormalization: (jobIndex: number, optionTitle: string) => void;
  handleResumeJobsDateChange: (newDateInfo: any, index: number) => void;
  mapApiDocumentsToAttachments: (documents: any[]) => any[];
}

const SubmittedWorkExperienceSection: React.FC<SubmittedWorkExperienceSectionProps> = ({
  jobs,
  editMode,
  locationOptions,
  currOptions,
  isRecommendationError,
  updateJob,
  handleChooseNormalization,
  handleResumeJobsDateChange,
  mapApiDocumentsToAttachments
}) => {
  const renderNormalizationOptions = (
    normalizationChoice: string,
    description: string
  ) => {
    return (
      <div className="normalization-option">
        <div className="normalization-option-title">
          <img
            src={CheckIcon}
            alt={"check-icon"}
            className={"normalization-option-title-active"}
          />
          {normalizationChoice}
        </div>
        {description && (
          <div className={"normalization-option-text"}>{description}</div>
        )}
      </div>
    );
  };

  const handleJobTitleChange = (e: React.ChangeEvent<HTMLInputElement>, index: number) => {
    if (editMode) {
      updateJob(index, 'title', e.target.value);
    }
  };

  const handleJobTitlePaste = (e: React.ClipboardEvent<HTMLInputElement>, index: number) => {
    if (editMode) {
      requestAnimationFrame(() => {
        const inputElements = document.querySelectorAll('input[aria-label="Job Title"]');
        if (inputElements && inputElements[index]) {
          const value = (inputElements[index] as HTMLInputElement).value;
          updateJob(index, 'title', value);
        }
      });
    }
  };

  const handleJobTitleBlur = (e: React.FocusEvent<HTMLInputElement>, index: number) => {
    if (editMode) {
      updateJob(index, 'title', e.target.value);
    }
  };

  const handleJobDescriptionChange = (e: React.ChangeEvent<HTMLTextAreaElement>, index: number) => {
    if (editMode) {
      updateJob(index, 'description', e.target.value);
    }
  };

  const handleJobDescriptionPaste = (e: React.ClipboardEvent<HTMLTextAreaElement>, index: number) => {
    if (editMode) {
      setTimeout(() => {
        const value = e.currentTarget.value;
        updateJob(index, 'description', value);
      }, 0);
    }
  };

  const handleJobDescriptionBlur = (e: React.FocusEvent<HTMLTextAreaElement>, index: number) => {
    if (editMode) {
      updateJob(index, 'description', e.target.value);
    }
  };

  return (
    <div>
      <div className="edit-resume-work-title">Work History</div>
      {jobs?.map((job: any, index: number) => (
        <div key={index} className="work-experience">
          <h4>Work Experience {index + 1}</h4>
          <InputKMQ
            value={job.company ?? ""}
            label={"Company"}
            onChange={(e) => editMode ? updateJob(index, 'company', e.target.value) : null}
            type="text"
            placeholder=""
          />
          <SelectKMQ
            options={locationOptions}
            value={
              job.location ? { label: job.location, value: job.location } : null
            }
            label={"Location"}
            onChange={(option) => editMode ? updateJob(index, 'location', option.value) : null}
          />
          <Checkbox
            active={job.current_job ?? false}
            text={"I am currently working in this position"}
            handleClick={() => editMode ? updateJob(index, 'current_job', !job.current_job) : null}
            disabled={!editMode}
          />
          <ResumeDates
            start_date={job.start_date}
            end_date={job.end_date}
            hideFutureDates={!(job.current_job ?? false)}
            changeResume={(newDateInfo) => handleResumeJobsDateChange(newDateInfo, index)}
            active={job.current_job ?? false}
          />
          <InputKMQ
            value={job.title ?? ""}
            label={"Job Title"}
            onChange={(e) => handleJobTitleChange(e, index)}
            onPaste={(e) => handleJobTitlePaste(e, index)}
            onBlur={(e) => handleJobTitleBlur(e, index)}
            type="text"
            placeholder=""
          />
          <TextArea
            value={job.description ?? ""}
            label={"Job Description"}
            onChange={(e) => handleJobDescriptionChange(e, index)}
            onPaste={(e) => handleJobDescriptionPaste(e, index)}
            onBlur={(e) => handleJobDescriptionBlur(e, index)}
            placeholder=""
          />

          {
            // @ts-ignore
            editMode && job.title && job.description && currOptions.jobs && currOptions.jobs[index]?.normalization_choices?.length > 0 ? (
              <div>
                <div
                  className={`work-experience-notification ${isRecommendationError && !job.normalization_choice ? "work-experience-notification-error" : ""
                    }`}
                >
                  <WarningCircle size={21} color={"#212121"} />
                  {job.normalization_choice === "None of the Above" ? "Can't find what you're looking for?" +
                    " Try editing your job title and description. This will generate new titles for you to choose" +
                    " from based on the most relevant options." :
                    "Please choose the best option that matches your job title. " +
                    "Choosing an option will enable us to match you accurately with courses."
                  }
                </div>
                {
                  // @ts-ignore
                  currOptions.jobs[index]?.normalization_choices.map((item: { title: string, description: string }, idx: number) => (
                    <NormalizationOption
                      key={idx}
                      title={item.title}
                      description={item.description}
                      onChoose={() => handleChooseNormalization(index, item.title)}
                      active={job.normalization_choice === item.title}
                    />
                  ))}

                <FileUpload
                  onSave={(value) => {
                    if (value && value.related_documents) {
                      updateJob(index, 'related_documents', value.related_documents);
                    }
                  }}
                  // @ts-ignore
                  files={mapApiDocumentsToAttachments(job.related_documents)}
                  optional
                  maxFiles={5}
                  showFilesFirst
                />
              </div>
            ) : job.normalization_choice && !editMode ? (
              renderNormalizationOptions(
                job.normalization_choice,
                job.normalization_choice_descriptions
              )
            ) : null}
        </div>
      ))}
    </div>
  );
};

export default SubmittedWorkExperienceSection;